/**
 * `/app/me/families` — the households this person is in, and the way to start another
 * (issues #301 and #360).
 *
 * The `familyId` is in the route (ADR 0021 decision 2), so the panel needs somewhere to get
 * one; the GET is it. **The list is not what makes a family readable** — every screen's request
 * is verified again by `resolveAssertedFamily`, which re-reads the membership, the role and
 * whether the family still exists. A switcher entry is an offer, not an authorization.
 *
 * **The copy is the server's**, on both calls. `nextStep` is the same sentence `get_started`
 * and every family-tool refusal give a person with nowhere to go (#158), and every refusal the
 * create can produce arrives as a `message` this app renders rather than replaces — see
 * `services/mcp/src/auth/memberships.ts` and `createFamily.ts`.
 */

import { type AppApi, appFetch } from "./client";

/** One family the caller belongs to, as the switcher needs it. */
export interface Membership {
  readonly familyId: string;
  /** `null` when the family's record is already gone — never a placeholder name. */
  readonly name: string | null;
  /**
   * `deleting` is a family an owner has disbanded whose erasure is still running. It stays
   * in the list on purpose: telling that member "you have no family, create one" sends them
   * to a create flow that refuses them, which is the loop #228 broke.
   */
  readonly status: "active" | "deleting";
  /**
   * **The caller's own standing in this household** — what the switcher shows beside the name
   * so two of them are tellable apart (issue #360, ADR 0023 decision 8).
   *
   * It is the caller's own `MEMBER#` row and nothing about anybody else's, which is what keeps
   * the wireframes' *"with Priya and Dan"* out of here: that would be a roster read per family
   * on an endpoint that answers about this person alone.
   *
   * `null` for a household being erased, where there is no standing left to name.
   */
  readonly role: "owner" | "adult" | null;
}

export interface Memberships {
  readonly families: readonly Membership[];
  /** The server's guidance when no family is open to them, and `null` when one is. */
  readonly nextStep: string | null;
}

export function fetchMemberships(api: AppApi): Promise<Memberships> {
  return appFetch<Memberships>(api, "/app/me/families");
}

/** What the server answers a create with — the household, and which account owns it. */
export interface StartedFamily {
  readonly familyId: string;
  readonly familyName: string | null;
  readonly role: "owner" | "adult";
  /**
   * True when this was a resubmit and the household already existed.
   *
   * It is **not an error state**: a double-clicked button and a lost response are the same
   * request from the server's side, and the honest answer is the household the first attempt
   * made. The panel lands them in it either way and only the wording differs.
   */
  readonly alreadyCreated: boolean;
  /** The account this household belongs to (ADR 0024 decision 2's disclosure). */
  readonly accountEmail: string;
}

/**
 * `POST /app/me/families` — start a household.
 *
 * **Its own route rather than the login app's**, because `/auth/*` admits the login origin
 * alone by a stated rule and widening that allowlist would have admitted the panel to
 * `/auth/onboard` and `/auth/authorize` as well. It commits through the same
 * `createFamilyForVerifiedIdentity` all three surfaces do (ADR 0024 decision 6).
 *
 * `timezone` is not cosmetic: due-date urgency is derived against the family's zone (ADR 0006
 * decision 9), so a household created without one silently takes the server's default and is
 * told things are due on the wrong day. Omitted rather than guessed when the browser will not
 * say, so the documented default applies instead.
 */
export function startFamily(
  api: AppApi,
  input: { name: string; displayName?: string },
): Promise<StartedFamily> {
  const timezone = browserTimezone();
  return appFetch<StartedFamily>(api, "/app/me/families", {
    method: "POST",
    body: {
      name: input.name,
      ...(input.displayName === undefined ? {} : { displayName: input.displayName }),
      ...(timezone === undefined ? {} : { timezone }),
    },
  });
}

/**
 * The browser's IANA timezone, or `undefined` if it will not say.
 *
 * Wrapped because `resolvedOptions()` is the one call in this submit that can throw on an
 * exotic runtime, and a household that could not be started because its timezone could not be
 * read would be an absurd failure. The same guard the login app's create carries.
 */
function browserTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === "string" && zone.length > 0 ? zone : undefined;
  } catch {
    return undefined;
  }
}

/** The families a member can actually open — what the switcher offers. */
export function openFamilies(memberships: Memberships): readonly Membership[] {
  return memberships.families.filter((family) => family.status === "active");
}
